<template>
    <ModalShell
        :title="`Add Family Member for ${employeeName}`"
        subtitle="Add an adult family member to this employee's account."
        width="w-full"
        body-class="sm:!pl-0"
        @keydown.enter="handleEnter"
    >
        <template #icon>
            <PhUsersThree :size="24" />
        </template>
        <template #header-action>
            <button type="button" class="close-btn" aria-label="Close" :disabled="isSubmitting" @click="closeDialog">
                <PhX :size="20" />
            </button>
        </template>

        <form class="w-full max-h-[60vh] overflow-y-auto" @submit.prevent="submitMember">
            <div class="form-grid">
                <div class="form-row form-row--span-full">
                    <label>Email <span class="text-red-500">*</span></label>
                    <PrimeInputText v-model.trim="form.email" type="email" placeholder="email@example.com" class="w-full" />
                </div>

                <div class="form-row form-row--span-2">
                    <label>First Name <span class="text-red-500">*</span></label>
                    <PrimeInputText v-model.trim="form.first_name" placeholder="First name" class="w-full" />
                </div>
                <div class="form-row">
                    <label>Middle</label>
                    <PrimeInputText v-model.trim="form.middle_name" placeholder="Middle" class="w-full" />
                </div>
                <div class="form-row form-row--span-2">
                    <label>Last Name <span class="text-red-500">*</span></label>
                    <PrimeInputText v-model.trim="form.last_name" placeholder="Last name" class="w-full" />
                </div>

                <div class="form-row form-row--span-full">
                    <label class="label-with-tag">
                        <span>Date of Birth</span>
                        <span class="optional-tag">Optional</span>
                    </label>
                    <button type="button" class="dob-trigger" @click="showDobPicker = !showDobPicker">
                        <PhCalendarBlank :size="18" class="text-zinc-500" />
                        <span :class="{ 'text-zinc-400': !form.dob }">
                            {{ formatDate(form.dob) || 'Pick a date' }}
                        </span>
                        <button
                            v-if="form.dob"
                            type="button"
                            class="dob-clear"
                            @click.stop="form.dob = ''"
                            aria-label="Clear date"
                        >
                            <PhX :size="14" />
                        </button>
                    </button>
                    <div v-if="showDobPicker" class="dob-inline">
                        <CustomDatePicker
                            :initial-date="dobInitialDate"
                            :min-age="18"
                            @cancel="showDobPicker = false"
                            @apply="onDobApplied"
                        />
                    </div>
                    <span v-if="dobError" class="form-error">{{ dobError }}</span>
                </div>

                <div class="form-row form-row--span-full">
                    <label class="label-with-tag">
                        <span>Phone</span>
                        <span class="optional-tag">Optional</span>
                    </label>
                    <PhoneFields v-model="form.phone" />
                </div>

                <div class="form-row form-row--span-full">
                    <label class="label-with-tag">
                        <span>Address</span>
                        <span class="optional-tag">Optional</span>
                    </label>
                    <AddressFields v-model="form.address" />
                </div>

                <div class="form-row form-row--span-full invite-row">
                    <PrimeCheckbox v-model="form.invite" inputId="family-invite" binary />
                    <label for="family-invite" class="invite-label">
                        Send a welcome email so they can set up their own account
                    </label>
                </div>
            </div>
        </form>

        <template #footer>
            <PrimeButton label="Cancel" severity="secondary" @click="closeDialog" :disabled="isSubmitting" />
            <PrimeButton
                label="Add Family Member"
                :loading="isSubmitting"
                :disabled="!canSubmit"
                @click="submitMember"
            />
        </template>
    </ModalShell>
</template>

<script setup lang="ts">
import {
    PhCalendarBlank,
    PhUsersThree,
    PhX,
} from '@phosphor-icons/vue';
import PrimeButton from 'primevue/button';
import PrimeCheckbox from 'primevue/checkbox';
import PrimeInputText from 'primevue/inputtext';
import ModalShell from '~/components/modals/shared/ModalShell.vue';
import AddressFields from '~/components/shared/forms/AddressFields.vue';
import PhoneFields from '~/components/shared/forms/PhoneFields.vue';
import CustomDatePicker from '~/components/main/shared/CustomDatePicker.vue';
import { isValidEmail } from '@privacy-bee/validate/email';
import { optionalPhonePayload, optionalPrefixedAddressPayload } from '~/utils/employee-contact-payload';
import { formatDate } from '@privacy-bee/format/date';
import { businessService } from '~/services/business-service';
import { enterFromField } from '~/utils/dialog-enter';
import { ModalNames } from '~/types/modals';
import { getDialogRef } from '~/composables/dynamic-dialog';
import { memberFacingMessage } from '~/utils/errors';

const dialogRef = getDialogRef(ModalNames.ADD_EMPLOYEE_FAMILY_MEMBER);
const { showSuccess, showError } = useGlobalToast();

const companyId = computed(() => dialogRef.value?.data?.companyId as number | undefined);
const employeeId = computed(() => dialogRef.value?.data?.employeeId as number | undefined);
const employeeName = computed(() => dialogRef.value?.data?.employeeName as string ?? 'this employee');

const isSubmitting = ref(false);
const memberCreated = ref(false);

const form = reactive({
    email: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    dob: '' as string,
    invite: false,
    phone: { country_code: '1', phone_number: '' },
    address: { line_1: '', line_2: '', city: '', region: '', postal_code: '', country: '' },
});

const showDobPicker = ref(false);

// Default to ~35 years ago — typical adult family member age, saves
// the admin from scrolling back from today's month.
const dobInitialDate = computed<Date>(() => {
    if (form.dob) return new Date(form.dob + 'T12:00:00');
    const now = new Date();
    return new Date(now.getFullYear() - 35, now.getMonth(), now.getDate(), 12, 0, 0);
});

const emailValid = computed(() => isValidEmail(form.email));

const dobError = computed<string | null>(() => {
    if (!form.dob) return null;
    const dob = new Date(form.dob + 'T12:00:00');
    if (Number.isNaN(dob.getTime())) return 'Invalid date';
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
    if (age < 18) return 'Family member must be 18 or older. Use Add Minor Child for under-18 dependents.';
    return null;
});

const canSubmit = computed(() => {
    if (isSubmitting.value) return false;
    if (!emailValid.value) return false;
    if (form.first_name.trim().length < 2) return false;
    if (form.last_name.trim().length < 2) return false;
    if (dobError.value) return false;
    return true;
});

function closeDialog() {
    // Lock every close path while the create request is in flight: closing
    // early makes the parent drawer refresh before createEmployeeAdultFamily()
    // finishes, so a successfully created member would stay hidden. The success
    // path still closes (memberCreated flips before isSubmitting clears).
    if (isSubmitting.value && !memberCreated.value) return;
    dialogRef.value?.close({ created: memberCreated.value });
}

// Enter submits from a field only: on Cancel or the date trigger it must do what the button says.
function handleEnter(event: KeyboardEvent) {
    if (!enterFromField(event)) return;
    event.preventDefault();
    if (canSubmit.value) submitMember();
}

function onDobApplied(value: Date) {
    const local = new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12, 0, 0);
    form.dob = `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(2, '0')}-${String(local.getDate()).padStart(2, '0')}`;
    showDobPicker.value = false;
}

async function submitMember() {
    if (!companyId.value || !employeeId.value || !canSubmit.value) return;
    isSubmitting.value = true;
    try {
        const payload: Parameters<typeof businessService.createEmployeeAdultFamily>[2] = {
            email: form.email.trim(),
            first_name: form.first_name.trim(),
            middle_name: form.middle_name.trim() || null,
            last_name: form.last_name.trim(),
            dob: form.dob || null,
            invite: form.invite,
        };

        // Phone and address are optional; the shared rule attaches each only when
        // it is filled in enough for the backend's required-with rules.
        payload.phone_number = optionalPhonePayload(form.phone);
        payload.address = optionalPrefixedAddressPayload(form.address);

        await businessService.createEmployeeAdultFamily(companyId.value, employeeId.value, payload);
        memberCreated.value = true;
        showSuccess('Family member added', `${form.first_name} has been added to ${employeeName.value}'s family.`);
        closeDialog();
    } catch (error) {
        showError('Error', memberFacingMessage(error, 'Failed to add family member.'));
    } finally {
        isSubmitting.value = false;
    }
}
</script>

<style lang="scss" scoped>
.close-btn {
    @apply flex h-8 w-8 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700;
}

.form-grid {
    @apply grid grid-cols-5 gap-3 pt-2;
}

.form-row {
    @apply col-span-1 flex flex-col;

    &--span-2 {
        @apply col-span-2;
    }

    &--span-full {
        @apply col-span-5;
    }

    label {
        @apply block text-sm font-medium text-zinc-700 mb-1.5;
    }
}

.form-error {
    @apply text-xs text-red-600 mt-1;
}

.dob-trigger {
    @apply w-full flex items-center gap-2 px-3 py-2 border border-zinc-300 rounded-md bg-white text-sm text-zinc-800 cursor-pointer transition-colors;

    &:hover {
        @apply border-zinc-400;
    }

    .dob-clear {
        @apply ml-auto p-1 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100;
    }
}

// Renders the date picker in flow under the DOB trigger. A subtle border keeps the
// boundary between picker and the rest of the form visible once it expands.
.dob-inline {
    @apply mt-2 rounded-lg border border-zinc-200 bg-white overflow-hidden;
    box-shadow: var(--shadow-overlay);
}

.label-with-tag {
    @apply flex items-center justify-between gap-2 #{!important};

    .optional-tag {
        @apply text-xs font-normal text-zinc-400;
    }
}

.invite-row {
    @apply flex-row items-center gap-2 mt-2;

    .invite-label {
        @apply text-sm text-zinc-700 mb-0 cursor-pointer;
    }
}
</style>
